import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '@/shared/api/client';
import { useActiveCountry } from '@/shared/countries/ActiveCountryProvider';
import type { CasaDolar } from '@/shared/lib/tipoCambio';

/**
 * Cliente del tablero operativo (Argentina). Tres fuentes: ventas, stock y
 * mantenimiento. Espeja los endpoints de `server/src/routes/dashboardVentas.ts`.
 */

// --------------------------------------------------------------------------
// Ventas
// --------------------------------------------------------------------------

export type VentasSeriePunto = {
  mes: string;
  label: string;
  total: number;
  unidades: number;
  /** Opcional: las series por tipo no lo traen. */
  operaciones?: number;
};

export type TipoVendido = { tipo: string; total: number; series: VentasSeriePunto[] };
export type ArticuloMes = {
  articulo: string;
  tipo: string;
  total: number;
  /** Series alineadas a `series[]` (mismo orden de meses del rango). */
  montoSerie: number[];
  unidadesSerie: number[];
};

export type MesEnCurso = {
  mes: string;
  label: string;
  diaCorte: number;
  total: number;
  totalPrev: number;
  unidades: number;
  unidadesPrev: number;
  operaciones: number;
  operacionesPrev: number;
  clientes: number;
  ticket: number;
};

export type PeriodoResumen = {
  total: number;
  unidades: number;
  operaciones: number;
  clientes: number;
  ticket: number;
  promedioMensual: number;
  meses: number;
};

export type CicloVenta = {
  cotizacionAPago: number | null;
  muestrasPago: number;
  cotizacionACierre: number | null;
  muestrasCierre: number;
};

export type TopCliente = { cliente: string; total: number; operaciones: number };
export type TopVendedor = { vendedor: string; total: number; unidades: number; operaciones: number };
export type TopArticulo = { articulo: string; categoria: string; unidades: number; total: number };

export type VentasDashboard = {
  moneda: string;
  pais: string;
  desde: string;
  hasta: string;
  diaCorte: number;
  series: VentasSeriePunto[];
  mesEnCurso: MesEnCurso;
  periodo: PeriodoResumen;
  ciclo: CicloVenta;
  topClientes: TopCliente[];
  topVendedores: TopVendedor[];
  categorias: string[];
  topArticulos: TopArticulo[];
  tiposVendidos: TipoVendido[];
  articulosMes: ArticuloMes[];
};

export type VentaLineaDetalle = {
  fechaPago: string;
  fecha: string;
  mesAnio: string;
  articulo: string;
  tipo: string;
  cliente: string;
  vendedor: string;
  unidades: number;
  monto: number;
};

export type VentasDetalleResponse = {
  moneda: string;
  desde: string;
  hasta: string;
  lineas: VentaLineaDetalle[];
};

export type RangoPreset = '3m' | '6m' | '12m' | 'ytd';

export const RANGO_LABELS: Record<RangoPreset, string> = {
  '3m': '3 meses',
  '6m': '6 meses',
  '12m': '12 meses',
  ytd: 'Año actual',
};

function rangoParams(preset: RangoPreset): { desde: string; hasta: string } {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth() + 1;
  const hasta = `${String(m).padStart(2, '0')}/${y}`;
  if (preset === 'ytd') return { desde: `01/${y}`, hasta };
  const back = preset === '3m' ? 2 : preset === '6m' ? 5 : 11;
  let dy = y;
  let dm = m - back;
  while (dm < 1) {
    dm += 12;
    dy -= 1;
  }
  return { desde: `${String(dm).padStart(2, '0')}/${dy}`, hasta };
}

export async function getVentasDashboard(preset: RangoPreset): Promise<VentasDashboard> {
  const { desde, hasta } = rangoParams(preset);
  const qs = new URLSearchParams({ desde, hasta }).toString();
  return apiFetch<VentasDashboard>(`/api/dashboard/ventas?${qs}`);
}

export function useVentasDashboard(preset: RangoPreset) {
  const { code } = useActiveCountry();
  return useQuery({
    queryKey: ['dashboard-ventas', code, preset],
    queryFn: () => getVentasDashboard(preset),
    staleTime: 60_000,
    enabled: code === 'AR',
  });
}

export async function getVentasDetalle(preset: RangoPreset): Promise<VentasDetalleResponse> {
  const { desde, hasta } = rangoParams(preset);
  const qs = new URLSearchParams({ desde, hasta }).toString();
  return apiFetch<VentasDetalleResponse>(`/api/dashboard/ventas-detalle?${qs}`);
}

/** Detalle a nivel línea. Se carga on-demand (al abrir el diálogo). */
export function useVentasDetalle(preset: RangoPreset, enabled: boolean) {
  const { code } = useActiveCountry();
  return useQuery({
    queryKey: ['dashboard-ventas-detalle', code, preset],
    queryFn: () => getVentasDetalle(preset),
    staleTime: 60_000,
    enabled: enabled && code === 'AR',
  });
}

// --------------------------------------------------------------------------
// Stock
// --------------------------------------------------------------------------

export type StockMonto = { unidades: number; valor: number };
export type StockEstado = {
  estado: string;
  unidades: number;
  valor: number;
  pct: number;
  integraSinVender: boolean;
};
export type StockTipo = {
  tipo: string;
  articulos: number;
  unidades: number;
  valor: number;
  pct: number;
};
export type StockDetalle = {
  articulo: string;
  tipo: string;
  modelo: string;
  precio: number | null;
  disponible: number;
  prestShow: number;
  reservado: number;
  reservadoParcial: number;
  pendienteDespacho: number;
  porIngresar: number;
  totalStock: number;
  valor: number;
  sinVender: number;
};

export type StockDashboard = {
  vista: 'ars' | 'usd';
  moneda: string;
  cotizacion: { casa: string; fecha: string; valor: number } | null;
  cotizacionNoDisponible: boolean;
  alFecha: string;
  articulos: number;
  sinPrecio: number;
  stockTotal: StockMonto;
  sinVender: StockMonto;
  disponible: StockMonto;
  porIngresar: StockMonto;
  prestamo: StockMonto;
  comprometido: StockMonto;
  porEstado: StockEstado[];
  porTipo: StockTipo[];
  detalle: StockDetalle[];
};

export type VistaStock = 'ars' | 'usd';

export async function getStockDashboard(vista: VistaStock, casa: CasaDolar): Promise<StockDashboard> {
  const qs = new URLSearchParams({ vista, casa }).toString();
  return apiFetch<StockDashboard>(`/api/dashboard/stock?${qs}`);
}

export function useStockDashboard(vista: VistaStock, casa: CasaDolar) {
  const { code } = useActiveCountry();
  return useQuery({
    queryKey: ['dashboard-stock', code, vista, casa],
    queryFn: () => getStockDashboard(vista, casa),
    staleTime: 60_000,
    enabled: code === 'AR',
  });
}

// --------------------------------------------------------------------------
// Mantenimiento
// --------------------------------------------------------------------------

export type MantenimientoPend = {
  id: string;
  cliente: string;
  fecha: string;
  dias: number;
  total: number;
};

export type MantenimientoDashboard = {
  pais: string;
  faltaDespachar: { count: number; ventas: MantenimientoPend[] };
  pendientePuestaMarcha: { count: number; ventas: MantenimientoPend[] };
  noCerrados: number;
  maxDiasPendiente: number;
};

export async function getMantenimientoDashboard(): Promise<MantenimientoDashboard> {
  return apiFetch<MantenimientoDashboard>('/api/dashboard/mantenimiento');
}

export function useMantenimientoDashboard() {
  const { code } = useActiveCountry();
  return useQuery({
    queryKey: ['dashboard-mantenimiento', code],
    queryFn: () => getMantenimientoDashboard(),
    staleTime: 60_000,
    enabled: code === 'AR',
  });
}

// --------------------------------------------------------------------------
// Reposición (qué comprar)
// --------------------------------------------------------------------------

export type ReposicionItem = {
  articulo: string;
  tipo: string;
  ventaMensual: number;
  disponible: number;
  porIngresar: number;
  cobertura: number;
  coberturaConIngreso: number;
  sugerido: number;
  valorSugerido: number | null;
  precio: number | null;
};

export type ReposicionDashboard = {
  moneda: string;
  meses: number;
  objetivo: number;
  resumen: {
    urgentes: number;
    cubiertos: number;
    reponer: number;
    unidadesUrgente: number;
    valorUrgente: number;
    valorReponer: number;
  };
  urgentes: ReposicionItem[];
  cubiertos: ReposicionItem[];
  reponer: ReposicionItem[];
};

export async function getReposicion(meses: number, objetivo: number): Promise<ReposicionDashboard> {
  const qs = new URLSearchParams({ meses: String(meses), objetivo: String(objetivo) }).toString();
  return apiFetch<ReposicionDashboard>(`/api/dashboard/reposicion?${qs}`);
}

export function useReposicion(meses: number, objetivo: number) {
  const { code } = useActiveCountry();
  return useQuery({
    queryKey: ['dashboard-reposicion', code, meses, objetivo],
    queryFn: () => getReposicion(meses, objetivo),
    staleTime: 60_000,
    enabled: code === 'AR',
  });
}

// --------------------------------------------------------------------------
// Vendedores (embudo por usuario de ventas)
// --------------------------------------------------------------------------

export type VendedorRow = {
  vendedor: string;
  realizadas: number;
  ganadas: number;
  anuladas: number;
  abiertas: number;
  abiertasViejas: number;
  montoGanado: number;
  conversion: number;
  cancelacion: number;
  ticketPromedio: number;
  tiempoCierre: number | null;
};

export type CotizacionMes = { mes: string; label: string; cotizaciones: number; ganadas: number };
export type AbiertaVieja = { vendedor: string; cliente: string; fecha: string; dias: number; monto: number };

export type VendedoresDashboard = {
  moneda: string;
  desde: string;
  hasta: string;
  umbralDias: number;
  vendedores: VendedorRow[];
  totales: {
    realizadas: number;
    ganadas: number;
    anuladas: number;
    abiertas: number;
    abiertasViejas: number;
    montoGanado: number;
    conversion: number;
    cancelacion: number;
  };
  cotizacionesPorMes: CotizacionMes[];
  abiertasViejas: AbiertaVieja[];
};

export async function getVendedores(preset: RangoPreset): Promise<VendedoresDashboard> {
  const { desde, hasta } = rangoParams(preset);
  const qs = new URLSearchParams({ desde, hasta }).toString();
  return apiFetch<VendedoresDashboard>(`/api/dashboard/vendedores?${qs}`);
}

export function useVendedores(preset: RangoPreset) {
  const { code } = useActiveCountry();
  return useQuery({
    queryKey: ['dashboard-vendedores', code, preset],
    queryFn: () => getVendedores(preset),
    staleTime: 60_000,
    enabled: code === 'AR',
  });
}
